import { useState, useRef } from 'react';
import clsx from 'clsx';

//Components
import Calendar from 'components/calendar/Calendar';
import { CalendarTypes } from 'shared/shared.models';
import NumberFormat from "react-number-format";
// import InputMask from "react-input-mask";
import {
    createStyles,
    fade,
    Theme,
    makeStyles,
} from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import { OutlinedInputProps } from "@material-ui/core/OutlinedInput";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-duotone-svg-icons';
import { useOutsideClick } from 'shared/shared.hooks';

//Styles
import Color from 'styles/variables.scss';
import './input.scss';


interface InputProps {
    inputType?: string;
    inputLabel?: string;
    inputHelperText?: string;
    inputValue?: string | number;
    maxLength?: number;
    inputPlaceholder?: string;
    format?: string;
    isNumber?: boolean;
    isDate?: boolean;
    autoFocus?: boolean;
    inputDisabled?: boolean;
    inputError?: boolean;
    startDate?: Date;
    maxDate?: Date;
    minDate?: Date;
    onEnter?: () => void;
    onChange?: (e) => void;
    onBlur?: () => void;
    onFocus?: () => void;
    selectedDate?: (date) => void;
}

interface NumberFormatCustomProps {
    inputRef?: (instance: NumberFormat | null) => void;
    onChange?: (event: { target: { name: string; value: string } }) => void;
    name?: string;
    format?: string;
}

const NumberFormatCustom = (props: NumberFormatCustomProps) => {
    const { inputRef, onChange, ...other } = props;
  
    return (
      <NumberFormat
        {...other}
        getInputRef={inputRef}
        onValueChange={values => {
          onChange({
            target: {
              name: props.name,
              value: values.value === " " ? null : values.value
            }
          });
        }}
        thousandSeparator
        isNumericString
        allowNegative={false}
        decimalScale={2}
      />
    );
}


const Input = (props: InputProps) => {
    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            root: {
                fontWeight: 600,
                border: `1px solid transparent`,
                overflow: "hidden",
                borderRadius: 4,
                color: Color.mainColor,
                backgroundColor: Color.twinkleBlue,
                transition: theme.transitions.create(["border", "box-shadow"]),

                "&:hover": {
                    backgroundColor: `#fff`,
                    boxShadow: `${fade("#6c5ce7", 0.25)} 0 0 0 1px`,
                    border: `1px solid #6c5ce7`
                },

                "&$focused": {
                    backgroundColor: `#fff`,
                    boxShadow: `${fade("#6c5ce7", 0.25)} 0 0 0 1px`,
                    border: `1px solid #6c5ce7`
                },

                "&$error": {
                    backgroundColor: `rgba(123, 114, 174, 0.25)`,
                    boxShadow: `${fade("#ee5253", 0.25)} 0 0 0 2px`,
                    border: `1px solid #ee5253`
                },

                "&$disabled": {
                    color: Color.mainColor,
                    backgroundColor: `rgba(123, 114, 174, 0.25)`
                }
            },
            focused: {},
            error: {},
            disabled: {}
        })
    );
    const classes = useStyles();
    const calendarRef = useRef();
    const [type, setType] = useState(props.inputType);
    const [openCalendar, setOpenCalendar] = useState(false);
    const isPassword = type === "password";

    const onEnter = (evt) => {
        const { key } = evt;

        if (key === "Enter" && props.onEnter) {
            props.onEnter();
        }
    }

    const closeCalendar = () => {
        setOpenCalendar(false);
    }
    useOutsideClick(calendarRef, closeCalendar);

    return (
        <div 
            ref={calendarRef}
            className={clsx("input-wrapper", props.isDate && "isDate")}
            onClick={() => {
                if (props.isDate) {
                    setOpenCalendar(true);
                }
            }}
        >
            {(props.isDate && openCalendar) && (
                <Calendar 
                    numberOfMonths={1}
                    type={CalendarTypes.SingleCalendar}
                    maxDate={props.maxDate}
                    minDate={props.minDate}
                    startDate={props.startDate}
                    showDateDisplay={false}
                    selectSingleDate={(date) => {
                        props.selectedDate(date);
                        setOpenCalendar(false);
                    }}
                />
            )}
            <label htmlFor="element">{props.inputLabel}</label>
            <div className="input-group">
                <TextField
                    autoFocus={props.autoFocus}
                    disabled={props.inputDisabled}
                    error={props.inputError}
                    placeholder={props.inputPlaceholder}
                    helperText={props.inputHelperText}
                    type={isPassword ? type : "text"}
                    fullWidth
                    variant="filled"
                    hiddenLabel={true}
                    inputProps={{
                        maxLength: props.maxLength,
                    }}
                    InputProps={{ 
                        classes, 
                        disableUnderline: true, 
                        readOnly: props.isDate,
                        ...(props.isNumber && { inputComponent: NumberFormatCustom as any  })
                    } as Partial<OutlinedInputProps>}
                    value={props.inputValue}
                    onKeyDown={onEnter}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    onFocus={props.onFocus}
                />
                {props.inputType === "password" ?
                    <div className={clsx("show-icon", props.inputHelperText && 'input-error')}>
                        {isPassword ?
                            <FontAwesomeIcon icon={faEye} onClick={() => setType("text")} /> : 
                            <FontAwesomeIcon icon={faEyeSlash} onClick={() => setType("password")} />
                        }
                    </div> : null
                }
            </div>
        </div>
    )
}

export default Input;
